import { cn } from '@/lib/utils'
import { ChevronRight, ChevronsUpDown, CircleMinus } from '@/components/icons'
import { Switch } from './switch'
import { NativeSelectOverlay } from './native-select-overlay'
import { CountryFlag } from './country-flag'
import { getIso2ByPhoneCode } from '@/lib/phone'

// The design system's drawer row input ("PRNTR Input" / Input Redux in
// Figma): a 48px label/value line with a hairline bottom border, optional
// icon slots, label pill, Auto pill, unit suffix and pop-up indicator.
// Editors embedded in a row are bare (borderless, right-aligned) — the row
// itself is the visual container. Textarea variants render as a label row
// with a surface-L2 field box below instead.

// Bare input styling shared by every editor embedded in a row. Font size
// stays at 16px (text-base) — anything smaller makes iOS zoom in on focus.
export const prntrInputClass =
  'w-full min-w-0 bg-transparent text-right text-base font-semibold text-content-primary-bold placeholder:text-content-primary-subtle-mid focus:outline-none'

// Small square pill: brand tone for label pills ("Info"), info tone for the
// computed-value "Auto" pill.
export function PrntrPill({
  tone = 'info',
  children,
}: {
  tone?: 'brand' | 'info'
  children: React.ReactNode
}) {
  return (
    <span
      className={cn(
        'shrink-0 rounded-[4px] p-1.5 text-xs font-semibold leading-4',
        tone === 'brand'
          ? 'bg-[rgba(200,157,191,0.3)] text-content-brand-bold'
          : 'bg-background-info-subtle text-content-info-bold'
      )}
    >
      {children}
    </span>
  )
}

// Right-side display value with the design's empty-state placeholder.
export function PrntrValue({
  value,
  placeholder = 'Select',
  tone = 'default',
  suffix,
}: {
  value: string | null | undefined
  placeholder?: string
  tone?: 'default' | 'info'
  suffix?: string
}) {
  return (
    <>
      <span
        className={cn(
          'min-w-0 truncate text-sm font-semibold',
          value
            ? tone === 'info'
              ? 'text-content-info-bold'
              : 'text-content-primary-bold'
            : 'text-content-primary-subtle-mid'
        )}
      >
        {value || placeholder}
      </span>
      {suffix && (
        <span
          className={cn(
            'shrink-0 text-xs font-semibold',
            value ? 'text-content-primary-subtle' : 'text-content-primary-subtle-mid'
          )}
        >
          {suffix}
        </span>
      )}
    </>
  )
}

const iconSlotClass =
  'flex shrink-0 items-center text-content-primary-subtle [&>svg]:size-4 [&_svg]:size-4'

// One label/value row. The right cluster is whatever the caller renders —
// a PrntrValue, a bare input, a switch…
export function PrntrInput({
  label,
  labelTone = 'default',
  labelPill,
  iconLeft,
  iconRight,
  autoPill,
  suffix,
  // Drives the suffix tone: subtle-mid while the row reads empty
  empty,
  error,
  onClick,
  className,
  children,
}: {
  label: React.ReactNode
  labelTone?: 'default' | 'info'
  labelPill?: React.ReactNode
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  autoPill?: boolean
  suffix?: string
  empty?: boolean
  // Submit-validation message: paints the row negative and renders below
  error?: string
  onClick?: () => void
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('border-b border-border-L1', className)}>
      <div
        className={cn('flex min-h-12 items-center gap-3', onClick && 'cursor-pointer')}
        onClick={onClick}
      >
        {iconLeft && <div className={cn(iconSlotClass, 'py-2 pr-2')}>{iconLeft}</div>}
        <div className="flex shrink-0 items-center gap-2">
          <span
            className={cn(
              'text-sm font-semibold',
              error
                ? 'text-content-negative-bold'
                : labelTone === 'info'
                  ? 'text-content-info-bold'
                  : 'text-content-primary-subtle'
            )}
          >
            {label}
          </span>
          {labelPill && <PrntrPill tone="brand">{labelPill}</PrntrPill>}
        </div>
        <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5">
          {autoPill && <PrntrPill tone="info">Auto</PrntrPill>}
          {children}
          {suffix && (
            <span
              className={cn(
                'shrink-0 text-xs font-semibold',
                empty ? 'text-content-primary-subtle-mid' : 'text-content-primary-subtle'
              )}
            >
              {suffix}
            </span>
          )}
        </div>
        {iconRight && <div className={cn(iconSlotClass, 'py-2 pl-2')}>{iconRight}</div>}
      </div>
      {error && <p className="pb-2 text-xs text-content-negative-bold">{error}</p>}
    </div>
  )
}

// Groups rows and strips the last row's hairline so section cards end clean.
export function PrntrGroup({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('flex flex-col [&>*:last-child]:border-b-0', className)}>{children}</div>
  )
}

// Pop-up select row: styled value + pop-up indicator with a transparent real
// <select> covering the row (NativeSelectOverlay), so iOS presents its native
// picker and the web gets the browser's own dropdown.
export function PrntrSelect<V extends string>({
  label,
  labelTone,
  labelPill,
  iconLeft,
  value,
  onChange,
  options,
  display,
  placeholder = 'Select',
  valueTone = 'default',
  readOnly,
  error,
}: {
  label: React.ReactNode
  labelTone?: 'default' | 'info'
  labelPill?: React.ReactNode
  iconLeft?: React.ReactNode
  value: V | null
  onChange: (next: V) => void
  options: readonly { value: V; label: string }[]
  // Visible value; defaults to the selected option's label
  display?: string
  placeholder?: string
  valueTone?: 'default' | 'info'
  readOnly?: boolean
  error?: string
}) {
  const selected = options.find((option) => option.value === value)
  return (
    <PrntrInput
      label={label}
      labelTone={labelTone}
      labelPill={labelPill}
      iconLeft={iconLeft}
      iconRight={
        readOnly ? undefined : <ChevronsUpDown className="size-4 text-content-primary-subtle" />
      }
      error={error}
      className="relative"
    >
      <PrntrValue
        value={display ?? selected?.label ?? null}
        placeholder={placeholder}
        tone={valueTone}
      />
      {!readOnly && (
        <NativeSelectOverlay
          value={value}
          onChange={onChange}
          options={options}
          ariaLabel={typeof label === 'string' ? label : undefined}
        />
      )}
    </PrntrInput>
  )
}

// Computed-value row ("Auto Row" in Figma): gray label, blue Auto pill +
// blue value on the right.
export function PrntrAutoRow({
  label,
  value,
  suffix,
}: {
  label: string
  value: string | null
  suffix?: string
}) {
  return (
    <div className="flex min-h-12 items-center justify-between gap-3 border-b border-border-L1">
      <span className="text-xs font-semibold text-content-primary-subtle">{label}</span>
      <div className="flex items-center gap-2">
        <PrntrPill tone="info">Auto</PrntrPill>
        <span className="text-sm font-semibold text-content-info-bold">
          {value ?? '–'}
          {suffix && value ? ` ${suffix}` : ''}
        </span>
      </div>
    </div>
  )
}

// Textarea variant: label row (no hairline) + a surface-L2 field box below.
// The caller passes the actual textarea as children, styled to fill the box.
export function PrntrTextareaShell({
  label,
  labelTone = 'default',
  labelPill,
  error,
  className,
  children,
}: {
  label: React.ReactNode
  labelTone?: 'default' | 'info'
  labelPill?: React.ReactNode
  error?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('flex flex-col pb-3', className)}>
      <div className="flex min-h-11 items-center gap-2">
        <span
          className={cn(
            'text-sm font-semibold',
            error
              ? 'text-content-negative-bold'
              : labelTone === 'info'
                ? 'text-content-info-bold'
                : 'text-content-primary-subtle'
          )}
        >
          {label}
        </span>
        {labelPill && <PrntrPill tone="brand">{labelPill}</PrntrPill>}
      </div>
      {children}
      {error && <p className="pt-1 text-xs text-content-negative-bold">{error}</p>}
    </div>
  )
}

// Field-box styling for the textarea itself (pair with PrntrTextareaShell).
// Text stays 16px — the iOS zoom rule again.
export const prntrTextareaClass =
  'min-h-[68px] rounded-[10px] border-border-L1 bg-surface-L2 p-3 text-base font-semibold shadow-none placeholder:text-content-primary-subtle-mid'

// Uppercase sub-section label (CREW, DEPARTURE, OIL UPLIFT, …)
export function SectionLabel({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <p
      className={cn(
        'pt-2 text-xs font-semibold uppercase leading-4 tracking-[1.5px] text-content-primary-subtle-mid',
        className
      )}
    >
      {children}
    </p>
  )
}

// Bordered section card: the chrome rows sit in outside the flight log's
// numbered StepSection. Optional uppercase title inside the card.
export function PrntrCard({
  title,
  className,
  children,
}: {
  title?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('rounded-2xl border border-border-L2 bg-surface-L0 px-3 py-1', className)}>
      {title && <SectionLabel className="pt-3">{title}</SectionLabel>}
      <PrntrGroup>{children}</PrntrGroup>
    </div>
  )
}

// Entity-selection row: displays the picked value and opens a picker drawer
// on tap (the caller owns the drawer + its open state). Chevron indicates
// the drill-in; a filled row with onClear shows a clear button instead.
export function PrntrPickerRow({
  label,
  labelTone,
  labelPill,
  iconLeft,
  value,
  placeholder = 'Select',
  valueTone = 'default',
  onClick,
  onClear,
  hideChevron,
  readOnly,
  error,
}: {
  label: React.ReactNode
  labelTone?: 'default' | 'info'
  labelPill?: React.ReactNode
  iconLeft?: React.ReactNode
  // Display string of the current selection
  value: string | null | undefined
  placeholder?: string
  valueTone?: 'default' | 'info'
  onClick: () => void
  onClear?: () => void
  // Drop the drill-in chevron (rows the design renders without one)
  hideChevron?: boolean
  readOnly?: boolean
  error?: string
}) {
  return (
    <PrntrInput
      label={label}
      labelTone={labelTone}
      labelPill={labelPill}
      iconLeft={iconLeft}
      error={error}
      onClick={readOnly ? undefined : onClick}
      iconRight={
        readOnly ? undefined : onClear && value ? (
          <button
            type="button"
            aria-label="Clear selection"
            className="flex items-center"
            onClick={(e) => {
              e.stopPropagation()
              onClear()
            }}
          >
            <CircleMinus className="size-4 text-content-primary-subtle" />
          </button>
        ) : hideChevron ? undefined : (
          <ChevronRight className="size-4 text-content-primary-subtle" />
        )
      }
    >
      <PrntrValue value={value} placeholder={readOnly ? '—' : placeholder} tone={valueTone} />
    </PrntrInput>
  )
}

// Toggle row (the Figma "showSwitch" variant): label left, switch right.
export function PrntrSwitchRow({
  label,
  labelTone,
  iconLeft,
  checked,
  onChange,
  readOnly,
  error,
}: {
  label: React.ReactNode
  labelTone?: 'default' | 'info'
  iconLeft?: React.ReactNode
  checked: boolean
  onChange: (next: boolean) => void
  readOnly?: boolean
  error?: string
}) {
  return (
    <PrntrInput label={label} labelTone={labelTone} iconLeft={iconLeft} error={error}>
      <Switch checked={checked} onCheckedChange={onChange} disabled={readOnly} />
    </PrntrInput>
  )
}

// Phone row: tappable dial-code cluster (flag + code → country-code picker,
// owned by the caller) followed by a bare tel input.
export function PrntrPhoneRow({
  label,
  code,
  onCodeClick,
  number,
  onNumberChange,
  onBlur,
  placeholder = 'Phone number',
  readOnly,
  error,
}: {
  label: React.ReactNode
  // Dial code incl. "+" (e.g. "+44")
  code: string
  onCodeClick: () => void
  number: string
  onNumberChange: (next: string) => void
  onBlur?: () => void
  placeholder?: string
  readOnly?: boolean
  error?: string
}) {
  const iso2 = getIso2ByPhoneCode(code, number)
  if (readOnly) {
    return (
      <PrntrInput label={label} error={error}>
        <PrntrValue value={number ? `${code} ${number}` : null} placeholder="—" />
      </PrntrInput>
    )
  }
  return (
    <PrntrInput label={label} error={error}>
      <button
        type="button"
        onClick={onCodeClick}
        className="flex shrink-0 items-center gap-1 text-sm font-semibold text-content-primary-bold"
      >
        <CountryFlag iso2={iso2} />
        {code}
      </button>
      <input
        type="tel"
        inputMode="tel"
        placeholder={placeholder}
        value={number}
        onChange={(e) => onNumberChange(e.target.value)}
        onBlur={onBlur}
        className={cn(prntrInputClass, 'w-32 shrink-0 grow-0')}
      />
    </PrntrInput>
  )
}
